import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { getRegionMeta } from '../utils/region';
type LiveOrder = {
  material: string;
  quantity: number;
  city: string;
  createdAt: Date;
};
const ORDER_TEMPLATES = [{
  material: '5/8" Minus',
  quantity: 12,
  fallbackCity: 'Tacoma'
}, {
  material: '3-Way Topsoil',
  quantity: 15,
  fallbackCity: 'Kent'
}, {
  material: 'Drain Rock',
  quantity: 10,
  fallbackCity: 'Puyallup'
}, {
  material: 'Medium Bark',
  quantity: 14,
  fallbackCity: 'Bellevue'
}, {
  material: 'Pea Gravel',
  quantity: 8,
  fallbackCity: 'Auburn'
}, {
  material: '5/8" Clear',
  quantity: 11,
  fallbackCity: 'Renton'
}] as const;
type LiveOrderTickerProps = {
  isDispatchActive?: boolean;
  opensIn?: string;
  deliveryMessage?: string;
};
export function LiveOrderTicker({
  isDispatchActive = true,
  opensIn = '',
  deliveryMessage = ''
}: LiveOrderTickerProps) {
  const [now, setNow] = useState(() => new Date());
  const orders = useMemo<LiveOrder[]>(() => {
    const cities = getRegionMeta().cities;
    const created = new Date();
    return ORDER_TEMPLATES.map((order, index) => {
      const createdAt = new Date(created);
      createdAt.setMinutes(createdAt.getMinutes() - (index * 19 + 6));
      return {
        material: order.material,
        quantity: order.quantity,
        city: cities[index % cities.length] || order.fallbackCity,
        createdAt
      };
    });
  }, []);
  useEffect(() => {
    // When dispatch closes, lock the relative time in place so this reads as
    // the last completed feed rather than implying orders are still arriving.
    setNow(new Date());
    if (!isDispatchActive) return;
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive]);
  const marqueeOrders = [...orders, ...orders];
  const lastOrder = orders[0];
  if (!isDispatchActive) {
    const mobileStatus = 'Dispatch closed · Order online 24/7';
    const desktopStatus = `Dispatch closed · Order online 24/7${deliveryMessage ? ` · ${deliveryMessage}` : ''}${opensIn ? ` · ${opensIn}` : ''} · Last delivery: ${lastOrder.quantity} yd ${lastOrder.material} → ${lastOrder.city}`;
    return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label={desktopStatus} data-id="element-1130" data-mp="pe4xagvd7dln">
        <span className="mr-2 flex h-3 shrink-0 items-end gap-0.5" aria-hidden="true" data-id="element-1131" data-mp="kgl7qkt7veee">
          <span className="h-1.5 w-0.5 rounded-full bg-white/30" data-id="element-1132" data-mp="ggumewmglqh9" />
          <span className="h-3 w-0.5 rounded-full bg-white/30" data-id="element-1133" data-mp="bfx50m99x0gt" />
          <span className="h-2 w-0.5 rounded-full bg-white/30" data-id="element-1134" data-mp="bijb0hgin7wx" />
        </span>
        <div className="min-w-0 overflow-hidden" data-id="element-1135" data-mp="6hc2uhago6pq">
          <motion.div className="flex w-max items-center whitespace-nowrap text-[11px] font-medium tracking-wide text-white/60" animate={{
          x: ['0%', '-50%']
        }} transition={{
          duration: 72,
          ease: 'linear',
          repeat: Infinity
        }} data-id="element-1136" data-mp="1iirovincrsq">
            {[mobileStatus, mobileStatus].map((status, index) => <span key={`mobile-status-${index}`} className="inline-flex items-center pr-10 sm:hidden" data-id="element-1137" data-mp="24ciw3wsn35ou" data-mpi={index}>
                {status}
              </span>)}
            {[desktopStatus, desktopStatus].map((status, index) => <span key={`desktop-status-${index}`} className="hidden items-center pr-12 sm:inline-flex" data-id="element-1138" data-mp="2d64c1jgjiszu" data-mpi={index}>
                {status}
              </span>)}
          </motion.div>
        </div>
      </div>;
  }
  return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label="Recent orders" data-id="element-1139" data-mp="1lfzm75a0hdr8">
      <div className="mr-2 flex shrink-0 items-center gap-1.5" data-id="element-1140" data-mp="1pg1kd4gjfp1r">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#00b774]" data-id="element-1141" data-mp="1udtcnzl3hd6h">Live</span>
        <span className="flex h-3 items-end gap-0.5" aria-hidden="true" data-id="element-1142" data-mp="1zezb8e1frunx">
          {[0, 1, 2].map(index => <motion.span key={index} className="w-0.5 rounded-full bg-[#00b774]" animate={{
          height: ['35%', '100%', '35%']
        }} transition={{
          duration: 1.15,
          repeat: Infinity,
          delay: index * 0.18,
          ease: 'easeInOut'
        }} data-id="element-1143" data-mp="35syktj3zw3xh" />)}
        </span>
      </div>
      <span className="mr-2 shrink-0 text-white/30" aria-hidden="true" data-id="element-1144" data-mp="imvnso363ldk">•</span>
      <div className="min-w-0 overflow-hidden" data-id="element-1145" data-mp="wlllo5xt9p2z">
        <motion.div className="flex w-max items-center whitespace-nowrap" animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: Math.max(52, orders.length * 9),
        ease: 'linear',
        repeat: Infinity
      }} data-id="element-1146" data-mp="wj8wb7pq4cfw">
          {marqueeOrders.map((order, index) => <span key={`${order.material}-${index}`} className="inline-flex items-center text-[11px] font-medium text-white/50 sm:text-xs" data-id="element-1147" data-mp="11k6cjer6awer" data-mpi={index}>
              <span className="text-white/50" data-id="element-1148" data-mp="1bahtq2zvb23l">{order.quantity} yd {order.material}</span>
              <span className="mx-1.5 text-[#00b774]" data-id="element-1149" data-mp="1pa0ozv7kyiyh">→</span>
              <span data-id="element-1150" data-mp="1pairh9ku5teu">{order.city}</span>
              <span className="mx-1.5 text-white/35" data-id="element-1151" data-mp="1kaxw194vk36v">·</span>
              <span className="text-white/35" data-id="element-1152" data-mp="1kchcazqj6e8n">{formatRelativeTime(order.createdAt, now)}</span>
              <span className="mx-4 text-white/25" data-id="element-1153" data-mp="23x961cnkliid">•</span>
            </span>)}
        </motion.div>
      </div>
    </div>;
}
function formatRelativeTime(createdAt: Date, now: Date): string {
  const minutes = Math.max(1, Math.round((now.getTime() - createdAt.getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.round(minutes / 60)} hr ago`;
}